import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  uploadPDF, 
  getPDF, 
  deletePDF, 
  listPDFs,
  getLayerDocument,
  PDFFile,
  PDFFileList,
  LayerDocument
} from '../api';

// Query keys for cache management
export const PDF_QUERY_KEYS = {
  all: ['pdf-editor'] as const,
  pdfs: () => [...PDF_QUERY_KEYS.all, 'pdfs'] as const,
  pdf: (id: string) => [...PDF_QUERY_KEYS.all, 'pdf', id] as const,
  pdfsByProject: (projectId: number) => [...PDF_QUERY_KEYS.all, 'pdfs', 'project', projectId] as const,
  layerDocument: (pdfId: string) => [...PDF_QUERY_KEYS.all, 'layer-document', pdfId] as const,
};

/**
 * Hook for uploading a PDF file
 */
export function useUploadPDF() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, projectId }: { file: File; projectId: number }) => 
      uploadPDF(file, projectId),
    onSuccess: (data) => {
      // Invalidate PDFs list to refetch
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.pdfs() });
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.pdfsByProject(data.project) });
      // Set the new PDF in cache
      queryClient.setQueryData(PDF_QUERY_KEYS.pdf(data.id), data);
    },
  });
}

/**
 * Hook for fetching a specific PDF file
 */
export function useGetPDF(pdfId: string | null) {
  return useQuery<PDFFile, Error>({
    queryKey: PDF_QUERY_KEYS.pdf(pdfId ?? ''),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return getPDF(pdfId);
    },
    enabled: Boolean(pdfId),
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Hook for listing all PDFs (optionally filtered by project)
 */
export function useListPDFs(projectId?: number) {
  return useQuery<PDFFileList[], Error>({
    queryKey: projectId ? PDF_QUERY_KEYS.pdfsByProject(projectId) : PDF_QUERY_KEYS.pdfs(),
    queryFn: () => listPDFs(projectId),
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Hook for deleting a PDF file
 */
export function useDeletePDF() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (pdfId: string) => deletePDF(pdfId),
    onSuccess: (_, pdfId) => {
      // Remove the PDF from cache
      queryClient.removeQueries({ queryKey: PDF_QUERY_KEYS.pdf(pdfId) });
      // Invalidate PDFs list
      queryClient.invalidateQueries({ queryKey: PDF_QUERY_KEYS.pdfs() });
    },
  });
}

/**
 * Hook for getting the layer document for a PDF
 */
export function useLayerDocument(pdfId: string | null) {
  return useQuery<LayerDocument, Error>({
    queryKey: PDF_QUERY_KEYS.layerDocument(pdfId ?? ''),
    queryFn: () => {
      if (!pdfId) throw new Error('PDF ID is required');
      return getLayerDocument(pdfId);
    },
    enabled: Boolean(pdfId),
    staleTime: 60_000, // 1 minute
  });
}

